import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { GENERATED_IMAGE_DIMENSIONS } from "../lib/image-constants";
import type { DownloadScale } from "../lib/image-download";
import { useBillingStatus } from "./BillingStatusContext";
import { Button } from "./Button";

interface DownloadDropdownProps {
  onSelect: (scale: DownloadScale) => void;
  isDownloading: boolean;
  label?: string;
  variant?: ComponentProps<typeof Button>["variant"];
  /** Replaces the default caret. Pass an icon for a text+icon trigger. */
  icon?: ReactNode;
  className?: string;
  /** Which way the menu opens. "up" suits a control pinned near the viewport
   *  bottom (gallery viewer); "down" suits one in a page header. */
  menuDirection?: "up" | "down";
}

const OPTIONS: { scale: DownloadScale; label: string }[] = [
  { scale: 1, label: "Original resolution" },
  { scale: 2, label: "Upscale 2x" },
  { scale: 4, label: "Upscale 4x" },
];

/** Upscaling runs through Topaz, which the free plan does not include at all —
 *  it is not something a spent allowance will restore next month. */
const isPaidOnly = (scale: DownloadScale) => scale > 1;

function optionLabel(scale: DownloadScale, label: string): string {
  if (scale === 1) return label;
  const { width, height } = GENERATED_IMAGE_DIMENSIONS;
  return `${label} (${width * scale} × ${height * scale})`;
}

export function DownloadDropdown({
  onSelect,
  isDownloading,
  label = "Download Image",
  variant,
  icon,
  className,
  menuDirection = "up",
}: DownloadDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const { billingStatus } = useBillingStatus();
  // Hidden rather than disabled: the menu would otherwise be mostly dead
  // options for every free user, and the pricing page is the place that
  // explains what upscaling is.
  const options = billingStatus?.has_active_subscription
    ? OPTIONS
    : OPTIONS.filter((option) => !isPaidOnly(option.scale));

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (scale: DownloadScale) => {
    setIsOpen(false);
    onSelect(scale);
  };

  return (
    <div ref={containerRef} className="relative inline-flex">
      {isOpen && (
        <div
          id={menuId}
          role="menu"
          className={`absolute right-0 z-20 min-w-[12rem] overflow-hidden rounded-lg border border-[var(--stroke-field)] bg-[var(--bg-modal)] shadow-lg ${
            menuDirection === "up" ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {options.map((option) => (
            <button
              key={option.scale}
              type="button"
              role="menuitem"
              onClick={() => handleSelect(option.scale)}
              className="block w-full px-4 py-2.5 text-left text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--accent-secondary-hover)]"
            >
              {optionLabel(option.scale, option.label)}
            </button>
          ))}
        </div>
      )}
      <Button
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={isDownloading}
        variant={variant}
        className={className}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
      >
        {isDownloading ? (
          <>
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[var(--stroke-field)] border-t-[var(--text-primary)]" />
            Preparing…
          </>
        ) : (
          <>
            {label}
            {icon ?? <span aria-hidden="true">▾</span>}
          </>
        )}
      </Button>
    </div>
  );
}
